/* ConceptStage.tsx — full-screen Sofi slide for 'concept' steps.

   Reuses the Onboarding kit (Panel / Dots / Btn / Sofi poses) so Sofi looks and
   moves exactly like she does in new-user onboarding. Used for the framing steps
   (what a sales ladder is, Fixed vs Adaptable) that teach a mental model before
   we drop the user onto the real controls. */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { C, POSE, Panel, Dots, Btn, Title } from '../Onboarding/onboardingKit';
import type { Pose } from '../Onboarding/onboardingKit';

interface Props {
  pose: Pose;
  title: string;
  body: ReactNode;
  stepIndex: number;
  stepCount: number;
  primaryLabel: string;
  primaryDisabled?: boolean;
  onPrimary: () => void;
  onBack: (() => void) | null;
  /** Skip the whole setup (null on the final slide, where it's meaningless). */
  onSkip: (() => void) | null;
  onExit: () => void;
}

export function ConceptStage({ pose, title, body, stepIndex, stepCount, primaryLabel, primaryDisabled, onPrimary, onBack, onSkip, onExit }: Props) {
  const { t } = useTranslation('common');
  return (
    <div
      className="om-concept"
      style={{
        position: 'fixed', inset: 0, zIndex: 4000, overflowY: 'auto',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(120% 120% at 78% 50%, color-mix(in srgb, var(--om-accent) 10%, transparent), color-mix(in srgb, var(--om-page) 86%, transparent) 55%)',
        backdropFilter: 'blur(6px)', fontFamily: "var(--font-sans)",
        padding: 24,
      }}
    >
      {/* Exit (skip whole setup) */}
      <button
        onClick={onExit}
        aria-label={t('tutorial.close')}
        style={{
          position: 'absolute', top: 20, right: 22, width: 34, height: 34, borderRadius: 10,
          background: 'color-mix(in srgb, var(--om-ink) 5%, transparent)', border: `1px solid ${C.line2}`, color: C.muted,
          cursor: 'pointer', fontSize: 18, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        ×
      </button>

      <div className="om-concept-row" style={{ display: 'flex', alignItems: 'flex-end', gap: 8, maxWidth: 940, minWidth: 0, margin: 'auto' }}>
        <div key={`${stepIndex}`} className="om-concept-panel" style={{ minWidth: 0, animation: 'om-rise .32s cubic-bezier(.4,0,.2,1)' }}>
          <Panel width={520}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 18 }}>
              <Dots n={stepCount} i={stepIndex} />
            </div>
            <Title h={title} />
            <div style={{ color: C.muted, fontSize: 15.5, lineHeight: 1.6, marginBottom: 26 }}>{body}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {onBack && <Btn kind="ghost" onClick={onBack}>{t('tutorial.back')}</Btn>}
              <div style={{ flex: 1 }} />
              {onSkip && (
                <button
                  onClick={onSkip}
                  style={{ background: 'transparent', border: 'none', color: C.faint, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, padding: '10px 6px' }}
                >
                  {t('tutorial.skipSetup')}
                </button>
              )}
              <Btn kind="primary" onClick={onPrimary} icon="arrow" disabled={primaryDisabled} style={primaryDisabled ? { opacity: 0.5, cursor: 'not-allowed', boxShadow: 'none' } : undefined}>{primaryLabel}</Btn>
            </div>
          </Panel>
        </div>

        {/* Sofi — big backdrop character to the right of the panel */}
        <img
          key={pose}
          className="om-concept-sofi"
          src={POSE[pose]}
          alt="Sofi"
          style={{
            width: 260, height: 'auto', marginBottom: -8, filter: 'drop-shadow(0 24px 60px color-mix(in srgb, var(--om-accent) 35%, transparent))',
            animation: 'om-pop .3s ease', userSelect: 'none', pointerEvents: 'none',
          }}
        />
      </div>

      <style>{`
        @keyframes om-rise { from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: none } }
        @keyframes om-pop { from { opacity: 0; transform: scale(.94) } to { opacity: 1; transform: none } }
        /* Phones: the panel takes the whole width (Sofi and the 520px panel side by side overflowed). */
        @media (max-width: 760px) {
          .om-concept { padding: 56px 12px 16px !important; align-items: flex-start !important; }
          .om-concept-row { width: 100%; }
          .om-concept-panel { width: 100%; }
          .om-concept .om-panel { padding: 20px 18px !important; border-radius: 18px !important; }
          .om-concept-sofi, .om-concept .om-panel > div:last-child { display: none; }
          .om-concept > button[aria-label] { top: 12px !important; right: 12px !important; }
        }
      `}</style>
    </div>
  );
}
